import React, { useState, useRef, useEffect } from "react";
import moment from "moment";
import { useMutation } from "@apollo/react-hooks";
import { gql } from "apollo-boost";

import AttachFileIcon from "@material-ui/icons/AttachFile";
import Button from "designV2/components/Button";
import { graphqlUrl } from "utils/config";

const SEND_MESSAGE = gql`
  mutation sendMessage($conversation: ID!, $messageType: MessageType!, $text: String) {
    sendMessage(sendMessageInput: { conversation: $conversation, messageType: $messageType, text: $text }) {
      _id
      text
    }
  }
`;

const IMAGE_EXTENSIONS = /\.(png|jpe?g|gif|webp|svg)$/i;

const readableError = (error) => {
  if (!error) return "";
  if (error.graphQLErrors && error.graphQLErrors.length > 0) {
    return error.graphQLErrors[0].message;
  }
  return error.message || "Something went wrong, please try again.";
};

// Uploads are stored as a path relative to the API host, so they need it
// prepended; anything already absolute is left alone.
const attachmentUrl = (text) =>
  /^https?:\/\//i.test(text) ? text : `${graphqlUrl}/${String(text).replace(/^\//, "")}`;

const renderMessageBody = (message) => {
  if (message.messageType === "Upload") {
    const url = attachmentUrl(message.text);
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" className="superTicketAttachment">
        {IMAGE_EXTENSIONS.test(message.text) ? (
          <img src={url} alt="attachment" className="superTicketAttachmentImage" />
        ) : (
          <span className="superTicketAttachmentLink">
            <AttachFileIcon style={{ fontSize: 16 }} /> View attachment
          </span>
        )}
      </a>
    );
  }

  // Structured messages arrive as a JSON string. The previous version
  // gated this on `typeof message.text === "object"`, which is never true
  // for a GraphQL String — so these always fell through and rendered raw
  // JSON at the reader.
  if (message.messageType === "Object" && typeof message.text === "string") {
    try {
      const parsed = JSON.parse(message.text);
      return (
        <div className="superTicketObjectMessage">
          {Object.keys(parsed).map((key) => (
            <div key={key} className="superTicketObjectRow">
              <span className="superTicketObjectKey">{key}</span>
              <span>{String(parsed[key])}</span>
            </div>
          ))}
        </div>
      );
    } catch (e) {
      return message.text;
    }
  }

  return message.text;
};

/**
 * Reply - the body of a ticket dialog: the thread plus a composer.
 *
 * Rendered inside a DialogShell by ConversationsList, which owns the
 * title, subtitle and close button, so this is content only — the
 * previous version drew its own header and close button inside a plain
 * MUI dialog.
 *
 * Sending a message appends it to the thread instead of closing the
 * dialog, which is what the old one did: you had to reopen the same
 * ticket to see your reply land or to send a follow-up.
 */
const Reply = ({ conversation, readOnly, onSent }) => {
  const bottomRef = useRef(null);
  const [messages, setMessages] = useState(conversation.messages || []);
  const [text, setText] = useState("");
  const [sendError, setSendError] = useState("");

  const [sendMessage, { loading: sending }] = useMutation(SEND_MESSAGE);

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView();
  }, [messages.length]);

  const onSend = () => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;

    setSendError("");
    sendMessage({
      variables: { conversation: conversation._id, messageType: "Text", text: trimmed },
    })
      .then((res) => {
        setMessages((current) => [
          ...current,
          {
            _id: res.data.sendMessage._id,
            text: res.data.sendMessage.text,
            senderType: "Admin",
            messageType: "Text",
            createdAt: new Date().toISOString(),
          },
        ]);
        setText("");
        if (onSent) onSent();
      })
      .catch((err) => setSendError(readableError(err)));
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" || e.charCode === 13) {
      e.preventDefault();
      onSend();
    }
  };

  const sortedMessages = [...messages].sort((a, b) => (b.createdAt > a.createdAt ? -1 : 1));

  // A ticket is answerable; a MESSAGE conversation is a driver/customer
  // thread the platform only observes.
  const canReply = !readOnly && conversation.conversationCategory === "SUPPORT_TICKET";

  return (
    <div className="superTicketThread">
      <div className="superTicketChat">
        {sortedMessages.map((message) => {
          const isAdmin = message.senderType === "Admin";
          const senderName =
            (message.user && message.user.fullName) || (message.driver && message.driver.fullName) || null;

          return (
            <div key={message._id} className={`superTicketBubbleRow ${isAdmin ? "isAdmin" : "isOther"}`}>
              <div className="superTicketSenderLabel">
                {message.senderType}
                {senderName && ` · ${senderName}`}
              </div>
              <div className="superTicketBubble">
                {renderMessageBody(message)}
                <span className="superTicketBubbleTime">
                  {moment(message.createdAt).format("MMM D, hh:mm A")}
                </span>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {sendError && (
        <div className="dsFormMessage error" role="alert">
          {sendError}
        </div>
      )}

      {canReply ? (
        <div className="superTicketComposer">
          <input
            type="text"
            className="dsInput"
            value={text}
            placeholder="Type your reply..."
            onChange={(e) => setText(e.target.value)}
            onKeyPress={handleKeyPress}
          />
          <Button variant="primary" onClick={onSend} disabled={!text.trim()} loading={sending}>
            Send
          </Button>
        </div>
      ) : (
        <div className="superTicketReadOnly">
          {readOnly
            ? "This conversation is closed."
            : "This is a conversation between a customer and a driver — read only."}
        </div>
      )}
    </div>
  );
};

export default Reply;
